import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import adminOpsService from '../../services/adminOpsService';
import {
  Workflow,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Copy,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Clock,
  Archive,
  Play,
  Pause,
  RefreshCw,
  Save,
  X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30', icon: CheckCircle },
  inactive: { label: 'Inactive', color: 'bg-slate-500/10 text-slate-400 border-slate-500/30', icon: Pause },
  draft: { label: 'Draft', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30', icon: Clock },
  archived: { label: 'Archived', color: 'bg-slate-600/10 text-slate-500 border-slate-600/30', icon: Archive },
};

const OPERATORS = [
  { value: 'eq', label: 'Equals' },
  { value: 'neq', label: 'Not Equals' },
  { value: 'gt', label: 'Greater Than' },
  { value: 'lt', label: 'Less Than' },
  { value: 'in', label: 'In List' },
  { value: 'contains', label: 'Contains' },
];

const RuleCard = ({ rule, onEdit, onDelete, onDuplicate, onToggleStatus }) => {
  const [showMenu, setShowMenu] = useState(false);
  const StatusIcon = STATUS_CONFIG[rule.status]?.icon || Clock;

  return (
    <div 
      className="bg-stone-900 border border-stone-800 rounded-xl p-5 hover:border-amber-500/30 transition-all group"
      data-testid={`rule-card-${rule.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
            <Workflow className="text-amber-400" size={20} />
          </div>
          <div>
            <h3 className="font-semibold text-white">{rule.name}</h3>
            <p className="text-xs text-slate-500">Priority: {rule.priority}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 text-xs rounded-full border ${STATUS_CONFIG[rule.status]?.color}`}>
            {STATUS_CONFIG[rule.status]?.label || rule.status}
          </span>
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1.5 rounded-lg text-slate-500 hover:text-white hover:bg-stone-800 transition-all"
            >
              <MoreVertical size={16} />
            </button>
            {showMenu && (
              <div className="absolute right-0 mt-1 w-40 bg-stone-800 border border-slate-700 rounded-lg shadow-xl z-10 py-1">
                <button
                  onClick={() => { onEdit(rule); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  <Edit size={14} /> Edit
                </button>
                <button
                  onClick={() => { onDuplicate(rule); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  <Copy size={14} /> Duplicate
                </button>
                <button
                  onClick={() => { onToggleStatus(rule); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700"
                >
                  {rule.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                  {rule.status === 'active' ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => { onDelete(rule); setShowMenu(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-slate-700"
                >
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {rule.description && (
        <p className="text-sm text-slate-400 mb-3 line-clamp-2">{rule.description}</p>
      )}

      {/* Conditions Summary */}
      <div className="mb-3">
        <p className="text-xs text-slate-500 mb-1">Conditions ({rule.conditions_logic || 'AND'})</p>
        <div className="flex flex-wrap gap-1">
          {(rule.conditions || []).slice(0, 3).map((cond, i) => (
            <span key={i} className="px-2 py-0.5 text-xs bg-stone-800 text-slate-400 rounded">
              {cond.field} {cond.operator} {JSON.stringify(cond.value).substring(0, 15)}
            </span>
          ))}
          {(rule.conditions || []).length > 3 && (
            <span className="px-2 py-0.5 text-xs bg-stone-800 text-slate-500 rounded">
              +{rule.conditions.length - 3} more
            </span>
          )}
        </div>
      </div>

      {/* Action */}
      <div className="flex items-center justify-between pt-3 border-t border-stone-800">
        <div className="flex items-center gap-2">
          <ChevronRight size={14} className="text-amber-400" />
          <span className="text-sm text-slate-300">{rule.output_action}</span>
        </div>
        <span className="text-xs text-slate-600">v{rule.version || 1}</span>
      </div>
    </div>
  );
};

const RuleEditor = ({ rule, onSave, onClose, isNew }) => {
  const [formData, setFormData] = useState({
    name: rule?.name || '',
    description: rule?.description || '',
    status: rule?.status || 'draft',
    priority: rule?.priority || 100,
    conditions: rule?.conditions || [],
    conditions_logic: rule?.conditions_logic || 'AND',
    output_action: rule?.output_action || 'use_method',
    output_params: rule?.output_params || {},
    notes: rule?.notes || '',
  });
  const [saving, setSaving] = useState(false);

  const addCondition = () => {
    setFormData(prev => ({
      ...prev,
      conditions: [...prev.conditions, { field: '', operator: 'eq', value: '' }],
    }));
  };

  const updateCondition = (index, key, value) => {
    const newConditions = [...formData.conditions];
    newConditions[index] = { ...newConditions[index], [key]: value };
    setFormData(prev => ({ ...prev, conditions: newConditions }));
  };

  const removeCondition = (index) => {
    setFormData(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.output_action) {
      toast.error('Name and output action are required');
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
      toast.success(isNew ? 'Rule created successfully' : 'Rule updated successfully');
      onClose();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl bg-stone-900 border-stone-800 text-white">
      <DialogHeader>
        <DialogTitle>{isNew ? 'Create Workflow Rule' : 'Edit Workflow Rule'}</DialogTitle>
        <DialogDescription className="text-slate-400">
          Define conditions and actions for automated workflow routing.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto py-4">
        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Rule Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Collection Account Handler"
              className="bg-stone-800 border-slate-700 text-white"
            />
          </div>
          <div>
            <label className="text-sm text-slate-400 mb-1 block">Priority</label>
            <Input
              type="number"
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 100 }))}
              className="bg-stone-800 border-slate-700 text-white"
            />
          </div>
        </div>

        <div>
          <label className="text-sm text-slate-400 mb-1 block">Description</label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="Describe what this rule does..."
            className="bg-stone-800 border-slate-700 text-white resize-none"
            rows={2}
          />
        </div>

        {/* Status */}
        <div>
          <label className="text-sm text-slate-400 mb-1 block">Status</label>
          <Select
            value={formData.status}
            onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
          >
            <SelectTrigger className="bg-stone-800 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-stone-800 border-slate-700">
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Conditions */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm text-slate-400">Conditions</label>
            <div className="flex items-center gap-2">
              <Select
                value={formData.conditions_logic}
                onValueChange={(value) => setFormData(prev => ({ ...prev, conditions_logic: value }))}
              >
                <SelectTrigger className="w-24 h-8 bg-stone-800 border-slate-700 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-stone-800 border-slate-700">
                  <SelectItem value="AND">AND</SelectItem>
                  <SelectItem value="OR">OR</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={addCondition}>
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {formData.conditions.map((cond, index) => (
              <div key={index} className="flex items-center gap-2 bg-stone-800 rounded-lg p-2">
                <Input
                  value={cond.field}
                  onChange={(e) => updateCondition(index, 'field', e.target.value)}
                  placeholder="Field (e.g., account_type)"
                  className="flex-1 bg-slate-700 border-slate-600 text-white text-sm h-8"
                />
                <Select
                  value={cond.operator}
                  onValueChange={(value) => updateCondition(index, 'operator', value)}
                >
                  <SelectTrigger className="w-32 h-8 bg-slate-700 border-slate-600 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-800 border-slate-700">
                    {OPERATORS.map(op => (
                      <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={typeof cond.value === 'string' ? cond.value : JSON.stringify(cond.value)}
                  onChange={(e) => updateCondition(index, 'value', e.target.value)}
                  placeholder="Value"
                  className="flex-1 bg-slate-700 border-slate-600 text-white text-sm h-8"
                />
                <button
                  onClick={() => removeCondition(index)}
                  className="p-1.5 text-slate-500 hover:text-red-400 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            {formData.conditions.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4 bg-stone-800 rounded-lg">
                No conditions defined. Click "Add" to create one.
              </p>
            )}
          </div>
        </div>

        {/* Output Action */}
        <div>
          <label className="text-sm text-slate-400 mb-1 block">Output Action *</label>
          <Select
            value={formData.output_action}
            onValueChange={(value) => setFormData(prev => ({ ...prev, output_action: value }))}
          >
            <SelectTrigger className="bg-stone-800 border-slate-700 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-stone-800 border-slate-700">
              <SelectItem value="use_method">Use Method</SelectItem>
              <SelectItem value="send_to_review">Send to Review</SelectItem>
              <SelectItem value="skip">Skip</SelectItem>
              <SelectItem value="flag">Flag for Attention</SelectItem>
              <SelectItem value="escalate">Escalate</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Notes */}
        <div>
          <label className="text-sm text-slate-400 mb-1 block">Internal Notes</label>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Any notes for future reference..."
            className="bg-stone-800 border-slate-700 text-white resize-none"
            rows={2}
          />
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={saving}>
          {saving ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
          {isNew ? 'Create Rule' : 'Save Changes'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};

const WorkflowRulesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingRule, setEditingRule] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [total, setTotal] = useState(0);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const data = await adminOpsService.listWorkflowRules(params);
      setRules(data.rules || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to load workflow rules');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, statusFilter]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleSave = async (formData) => {
    adminOpsService.setAuthHeaders(getAuthHeaders());
    if (editingRule) {
      await adminOpsService.updateWorkflowRule(editingRule.id, formData);
    } else {
      await adminOpsService.createWorkflowRule(formData);
    }
    fetchRules();
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Are you sure you want to archive "${rule.name}"?`)) return;
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      await adminOpsService.deleteWorkflowRule(rule.id);
      toast.success('Rule archived');
      fetchRules();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleDuplicate = (rule) => {
    setEditingRule(null);
    setIsCreating(true);
    // Pre-fill with duplicated data (without id)
    setTimeout(() => {
      setEditingRule({
        ...rule,
        id: null,
        name: `${rule.name} (Copy)`,
        status: 'draft',
      });
    }, 0);
  };

  const handleToggleStatus = async (rule) => {
    try {
      adminOpsService.setAuthHeaders(getAuthHeaders());
      const newStatus = rule.status === 'active' ? 'inactive' : 'active';
      await adminOpsService.updateWorkflowRule(rule.id, { status: newStatus });
      toast.success(`Rule ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchRules();
    } catch (error) {
      toast.error('Failed to update rule status');
    }
  };

  const filteredRules = rules.filter(rule =>
    rule.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rule.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="workflow-rules-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Workflow className="text-amber-400" size={28} />
            Workflow Rules
          </h1>
          <p className="text-slate-500 mt-1">
            Configure automated routing and decision rules for dispute processing
          </p>
        </div>
        <Button onClick={() => { setEditingRule(null); setIsCreating(true); }}>
          <Plus size={18} className="mr-2" />
          New Rule
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search rules..."
            className="pl-10 bg-stone-900 border-stone-800 text-white placeholder:text-slate-500"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-stone-900 border-stone-800 text-white">
            <Filter size={16} className="mr-2 text-slate-500" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-stone-800 border-slate-700">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={fetchRules}>
          <RefreshCw size={18} />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Total Rules</p>
          <p className="text-2xl font-bold text-white">{total}</p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Active</p>
          <p className="text-2xl font-bold text-emerald-400">
            {rules.filter(r => r.status === 'active').length}
          </p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Draft</p>
          <p className="text-2xl font-bold text-amber-400">
            {rules.filter(r => r.status === 'draft').length}
          </p>
        </div>
        <div className="bg-stone-900 border border-stone-800 rounded-lg p-4">
          <p className="text-slate-500 text-sm">Inactive</p>
          <p className="text-2xl font-bold text-slate-400">
            {rules.filter(r => r.status === 'inactive').length}
          </p>
        </div>
      </div>

      {/* Rules Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-48 bg-stone-800" />)}
        </div>
      ) : filteredRules.length === 0 ? (
        <div className="text-center py-16 bg-stone-900 rounded-xl border border-stone-800">
          <Workflow className="mx-auto text-slate-600 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-white mb-2">No Rules Found</h3>
          <p className="text-slate-500 mb-4">
            {searchQuery ? 'No rules match your search.' : 'Create your first workflow rule to get started.'}
          </p>
          <Button onClick={() => { setEditingRule(null); setIsCreating(true); }}>
            <Plus size={18} className="mr-2" />
            Create Rule
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredRules.map(rule => (
            <RuleCard
              key={rule.id}
              rule={rule}
              onEdit={setEditingRule}
              onDelete={handleDelete}
              onDuplicate={handleDuplicate}
              onToggleStatus={handleToggleStatus}
            />
          ))}
        </div>
      )}

      {/* Editor Dialog */}
      <Dialog open={isCreating || !!editingRule} onOpenChange={(open) => { if (!open) { setIsCreating(false); setEditingRule(null); } }}>
        <RuleEditor
          rule={editingRule}
          onSave={handleSave}
          onClose={() => { setIsCreating(false); setEditingRule(null); }}
          isNew={isCreating && !editingRule?.id}
        />
      </Dialog>
    </div>
  );
};

export default WorkflowRulesPage;
